import { IssueLabel } from "~/components/issueDetailsPage/IssueLabel"
import { Field, FieldError, FieldLabel } from "~/components/ui/field"
import { toast } from "sonner"
import { labelSchema } from "~/lib/schemas"
import { PlusIcon } from "lucide-react"
import { type ReactNode, useEffect, useState } from "react"
import { ColorPicker } from "~/components/ColorPicker"
import { Button } from "~/components/ui/button"
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from "~/components/ui/dialog"
import { Input } from "~/components/ui/input"

import { useTeam } from "~/hooks/useTeam"
import { api } from "~/trpc/react"

export function LabelCreateDialog({
	children,
	name
}: {
	children: ReactNode
	name?: string
}) {
	const { team } = useTeam()
	const [newLabelName, setNewLabelName] = useState("")
	const [newLabelColor, setNewLabelColor] = useState("#6366F1")
	const [open, setOpen] = useState(false)

	const utils = api.useUtils()
	const validation = labelSchema.safeParse({
		name: newLabelName,
		color: newLabelColor,
		teamId: team?.id ?? ""
	})

	useEffect(() => {
		if (open && newLabelName === "") {
			setNewLabelName(name || "")
		}
	}, [open, name, newLabelName])

	const { mutate: createLabel, isPending } = api.labels.create.useMutation({
		onSuccess: () => {
			utils.labels.list.invalidate({ teamId: team?.id ?? "" })
			setNewLabelName("")
			setOpen(false)
		},
		onError: (error) => toast.error(error.message || "Unable to create label.")
	})

	const handleCreateLabel = () => {
		if (!team?.id || !validation.success) return
		createLabel(validation.data)
	}

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>{children}</DialogTrigger>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Create Label</DialogTitle>
				</DialogHeader>
				<div className="flex items-center gap-4">
					<Field>
						<FieldLabel>Name</FieldLabel>
						<Input
							value={newLabelName}
							onChange={(e) => setNewLabelName(e.target.value)}
							placeholder="Enter label name"
							aria-invalid={!!newLabelName && !validation.success}
						/>
						{newLabelName && !validation.success && (
							<FieldError>{validation.error.issues[0]?.message}</FieldError>
						)}
					</Field>
					<Field>
						<FieldLabel>Colour</FieldLabel>
						<ColorPicker color={newLabelColor} onChange={setNewLabelColor} />
					</Field>
				</div>
				<Field>
					<FieldLabel>Preview</FieldLabel>
					<div>
						<IssueLabel
							label={{
								id: "preview",
								name: newLabelName.trim() || "Label name",
								color: newLabelColor
							}}
						/>
					</div>
				</Field>
				<Button
					onClick={handleCreateLabel}
					disabled={!team?.id || !validation.success}
					pending={isPending}
					pendingLabel="Creating…"
					className="mb-0.5"
				>
					<PlusIcon className="h-4 w-4" />
					Create
				</Button>
			</DialogContent>
		</Dialog>
	)
}
